import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { last } from 'lib/array/arrayUtils'

interface BundledDates {
  hotelCheckIn?: string
  hotelCheckOut?: string
  flightDepartureDate?: string
  flightReturnDate?: string
}

interface GetBundledDatesParams {
  isBundled: boolean
  startDate?: string
  endDate?: string
  itemViews: Array<{ item: App.Checkout.AnyItem }>
  flightItems: Array<App.Checkout.FlightItem>
  journeysById: Record<string, App.AnyJourney>
}

export function getBundledDates(params: GetBundledDatesParams): BundledDates {
  const { isBundled, startDate, endDate, itemViews, flightItems, journeysById } = params

  if (!isBundled) {
    return {
      hotelCheckIn: startDate,
      hotelCheckOut: endDate,
      flightDepartureDate: undefined,
      flightReturnDate: undefined,
    }
  }

  // Find the accommodation item ID
  const accommodationItemId = itemViews[0]?.item?.itemId
  if (!accommodationItemId) {
    return {
      hotelCheckIn: undefined,
      hotelCheckOut: undefined,
      flightDepartureDate: undefined,
      flightReturnDate: undefined,
    }
  }

  // Find the flight item that bundles with this accommodation item
  const bundledFlightItems = flightItems.filter((flightItem: App.Checkout.FlightItem) =>
    flightItem.bundledItemIds?.includes(accommodationItemId),
  )

  const bundledFlightItem = bundledFlightItems[0]

  // Get hotel dates from accommodation item
  const accommodationItem = itemViews[0]?.item
  const hotelCheckIn = isInstantBookingLEHotelItem(accommodationItem) ? accommodationItem.checkIn : undefined
  const hotelCheckOut = isInstantBookingLEHotelItem(accommodationItem) ? accommodationItem.checkOut : undefined

  if (bundledFlightItems.some(item => item.charterBundleRoute && item.fareType === 'oneWay')) {
    let flightDepartureDate = hotelCheckIn
    let flightReturnDate = hotelCheckOut
    for (const item of bundledFlightItems) {
      for (const flight of item.flights) {
        const journeyKey = item.searchId && getJourneyV2IdKey(flight.journeyId, item.searchId, flight.fareFamily?.id)
        const journey = (journeyKey ? journeysById[journeyKey] : undefined) as App.JourneyV2 | undefined
        const arrivalDate = journey?.flightGroup.flights.at(-1)?.arrivalDate
        if (flight.departingDate && (!flightDepartureDate || flight.departingDate < flightDepartureDate)) {
          flightDepartureDate = flight.departingDate
        }
        if (arrivalDate && (!flightReturnDate || arrivalDate > flightReturnDate)) {
          flightReturnDate = arrivalDate
        }
      }
    }
    return { hotelCheckIn, hotelCheckOut, flightDepartureDate, flightReturnDate }
  }

  // Get flight dates from flight item
  const departureFlight = bundledFlightItem?.flights?.[0]
  const flightDepartureDate = departureFlight?.departingDate

  // Get return flight arrival date (when flight arrives back at origin)
  const returnFlight = bundledFlightItem?.flights?.[1]
  let flightReturnDate: string | undefined
  if (returnFlight?.journeyId && bundledFlightItem?.searchId) {
    const journeyKey = getJourneyV2IdKey(returnFlight.journeyId, bundledFlightItem.searchId, returnFlight.fareFamily?.id)
    const returnJourney = journeysById[journeyKey] as App.JourneyV2 | undefined
    const returnFlightSegments = returnJourney?.flightGroup?.flights
    const lastReturnSegment = returnFlightSegments && returnFlightSegments.length > 0
      ? last(returnFlightSegments)
      : undefined
    flightReturnDate = lastReturnSegment?.arrivalDate
  }

  return {
    hotelCheckIn,
    hotelCheckOut,
    flightDepartureDate,
    flightReturnDate,
  }
}
